import type { AxiosInstance, AxiosRequestConfig } from "axios";
import type { ApiListResponse, Mixed } from "../../commonTypes";
import type { Webhook, WebhookId, WebhookMetrics } from "./Webhook";
import type { LeadId } from "../Lead/Lead";
import type { RequestSchedule } from "../RequestSchedule/RequestSchedule";
import type { TeamId } from "../Team/Team";
import type { UserId } from "../User/User";

export type WebhookFilter = {
  query?: string
  id?: Mixed<WebhookId>
  teamId?: Mixed<TeamId>
  userId?: Mixed<UserId>
  status?: Mixed<Webhook["status"]>
  event?: Mixed<Webhook["event"]>
  name?: Mixed<Webhook["name"]>
  withDeleted?: boolean
}

export const createWebhookApi = (axios: AxiosInstance) => {
  const getWebhooks = (
    params: {
      filter?: WebhookFilter
      limit: number
      offset: number
    },
    config?: AxiosRequestConfig,
  ) => {
    return axios.get<ApiListResponse<Webhook>>(
      "/api/webhooks",
      {
        ...config,
        params: {
          limit: params.limit,
          offset: params.offset,
          filter: {
            q: params.filter?.query,
            uuid: params.filter?.id,
            team_id: params.filter?.teamId,
            user_id: params.filter?.userId,
            event: params.filter?.event,
            name: params.filter?.name,
            status: params.filter?.status,
            deleted: params.filter?.withDeleted,
          },
        },
      },
    );
  };

  const createWebhook = async (params: Pick<Webhook, "name" | "event" | "target_url" | "filters" | "request_method">) => {
    return axios.post<Webhook>("/api/webhooks", params);
  };

  const updateWebhook = async (params: Pick<Webhook, "name" | "event" | "target_url" | "filters" | "request_method" | "status" | "uuid">) => {
    return axios.put<Webhook>(`/api/webhooks/${params.uuid}`, params);
  };

  const deleteWebhook = async (params: {
    id: WebhookId
  }) => {
    return axios.delete<undefined>(`/api/webhooks/${params.id}`);
  };

  const getWebhookMetrics = <Metrics extends Array<keyof WebhookMetrics>>(params: {
    ids: WebhookId[]
    metrics: Metrics
  }) => {
    return axios.post<Record<WebhookId, {[key in Metrics[number]]: WebhookMetrics[key]}>>("/api/webhooks/metrics", {
      uuids: params.ids,
      metrics: params.metrics,
    });
  };

  const testWebhook = async (params: {
    targetUrl: string
    requestMethod: Webhook["request_method"]
    event: Webhook["event"]
    leadId?: LeadId
  }) => {
    return axios.post<RequestSchedule>("/api/webhooks/test", {
      target_url: params.targetUrl,
      request_method: params.requestMethod,
      event: params.event,
      lead_uuid: params.leadId,
    });
  };

  return {
    getWebhooks,
    createWebhook,
    updateWebhook,
    deleteWebhook,
    getWebhookMetrics,
    testWebhook,
  };
};
